import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@aqarmap/aqarmap-ui';
import { useAppDispatch } from '~/redux/hooks';
import useBreakpoint from '~/hooks/useBreakpoint';
import { setFooterDisplay } from '~/redux/features/uiState/uiStateSlice';
import { useFullRoutes, useQuery } from '~/hooks';
import { SelectedPaymentType } from '~/types/paymentTypes';
import PurchasePaymentMethod from '~/components/PurchasePaymentMethod';
import PaymentSummaryWrapper from '~/components/PaymentSummaryWrapper';
import PaymentSummary from '~myAccount/pages/Wallet/PaymentSummary';
import '~/cardsIcons.css';
import { RequestStatusEnum } from '~/constants/enums';
import {
  PaymentMethodEnum,
  PaymentOperationTypeEnum,
} from '~/constants/enums/paymentEnums';
import { UserCardFormHandle } from '~/components/UserCardForm/types';
import { usePostPaymentPayMutation } from '~/redux/api/PaymentApiSlice';

export default function Topup() {
  const userCardFormRef = useRef<UserCardFormHandle>(null);
  const { MY_ACCOUNT_URL } = useFullRoutes();
  const dispatch = useAppDispatch();
  const { status, amount } = useQuery();
  const [checkoutError, setCheckoutError] = useState<string>('');
  const { mobileScreen } = useBreakpoint();
  const { t } = useTranslation('app');
  const [selectedPayment, setSelectedPayment] = useState<
    SelectedPaymentType | undefined
  >(undefined);
  const [isPayfortLoading, setIsPayfortLoading] = useState<boolean>(false);

  const [postPaymentPay, { isLoading: isTopupLoading }] =
    usePostPaymentPayMutation();

  const handlePayWithSavedCard = async () => {
    if (!selectedPayment) return;
    const response = await postPaymentPay({
      paymentMethod: PaymentMethodEnum.SAVED_CARD,
      cardId: selectedPayment?.id,
      operationType: PaymentOperationTypeEnum.WALLET_TOP_UP,
      amount: Number(amount),
    });
    const { data, error } = response;
    if (data?.status === 'success') {
      window.location.href = `${process.env.REACT_APP_BASE_URL}${MY_ACCOUNT_URL.WALLET.ROOT}`;
    } else if (data?.status === '3ds_required' && data?.['3ds_url']) {
      window.location.href = data['3ds_url'];
    } else if (data?.message) {
      setCheckoutError(t(data?.message));
    } else if (error) {
      setCheckoutError(
        t(
          (error as { data?: { message?: string } })?.data?.message ||
            'somethingWrong',
        ),
      );
    } else {
      setCheckoutError(t('somethingWrong'));
    }
  };

  const onCheckoutSubmit = () => {
    const isNewCard = selectedPayment?.isNew || false;
    const isValidUserCardForm = userCardFormRef?.current;
    if (isNewCard && isValidUserCardForm) {
      setIsPayfortLoading(true);
      userCardFormRef.current.submitUserCard({
        amount: Number(amount),
        operationType: PaymentOperationTypeEnum.WALLET_TOP_UP,
        returnTo: `${process.env.REACT_APP_BASE_URL}${MY_ACCOUNT_URL.WALLET.TOPUP(Number(amount))}`,
      });
    } else {
      handlePayWithSavedCard();
    }
  };

  const handleStatusFallback = () => {
    switch (status) {
      case RequestStatusEnum.FAILED:
        setCheckoutError(t('somethingWrong'));
        setSelectedPayment({
          isNew: true,
          id: 0,
        });
        break;
      case RequestStatusEnum.SUCCESS:
        window.location.href = `${process.env.REACT_APP_BASE_URL}${MY_ACCOUNT_URL.WALLET.ROOT}`;
        break;
    }
  };

  useEffect(() => {
    if (mobileScreen) {
      dispatch(setFooterDisplay('hide'));
    }
  }, [mobileScreen]);

  useEffect(() => {
    handleStatusFallback();
  }, [status]);

  useEffect(() => {
    document.title = `${t('topup')}|${t('aqarmap')}`;
  }, []);

  useEffect(() => {
    if (mobileScreen) {
      dispatch(setFooterDisplay('hide'));
    }
  }, [mobileScreen]);

  return (
    <div className="layout-boundary pt-4x pb-[40px] md:pb-[80px]">
      <div className="layout-boundary__inner flex flex-col-reverse md:flex-row gap-4x justify-between px-2x md:px-0">
        <PurchasePaymentMethod
          title={t('topupDetails')}
          setSelectedPayment={setSelectedPayment}
          selectedPayment={selectedPayment}
          error={checkoutError}
          newCardProps={{
            onFailure: () => setCheckoutError(t('somethingWrong')),
            ref: userCardFormRef,
          }}
        />
        <PaymentSummaryWrapper
          totalFeesLabel={t('totalFees')}
          submitBtnProps={{
            onClick: onCheckoutSubmit,
            isLoading: isTopupLoading || isPayfortLoading,
            disabled: !selectedPayment || isTopupLoading || isPayfortLoading,
          }}
          fees={
            <Trans
              i18nKey="priceValue"
              t={t}
              values={{
                value: amount,
              }}
              components={{
                tag: <span>placeholder</span>,
              }}
            />
          }
          summary={<PaymentSummary amount={Number(amount)} />}
          title={t('topupSummary')}
        />
        <div className="flex z-[40] md:hidden justify-between items-center  fixed bottom-0 left-0 w-full pt-2x pb-3x bg-white px-2x  border-t border-gray">
          <Button
            size="medium"
            variant="primary"
            className="w-full md:hidden"
            onClick={onCheckoutSubmit}
            isLoading={isTopupLoading || isPayfortLoading}
            disabled={!selectedPayment || isTopupLoading || isPayfortLoading}
          >
            {t('pay')}
          </Button>
        </div>
      </div>
    </div>
  );
}
